import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core";
import {
  Close as CloseIcon,
  Undo as UndoIcon,
  Delete as DeleteIcon,
} from "@material-ui/icons";
import { LoadingButton } from "@material-ui/lab";
import Alert from "@material-ui/lab/Alert";
import { makeStyles } from "@material-ui/styles";

// RTK Query
import {
  useCerrarAbonoMutation,
  useObtenerCategoriasContablesQuery,
} from "src/services/conciliacionApi";

// Helpers
import {
  formatearMonto,
  formatearRutDisplay,
  getBancoConfig,
} from "src/utils/conciliacionHelpers";
import { formatearFechaDisplay } from "src/utils/formatTime";

// Snackbar
import { useSnackbar } from "src/components/snackbar";

// ==================== ESTILOS ====================

const useStyles = makeStyles((theme) => ({
  dialogTitleReembolso: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2),
    backgroundColor: "#2196F3",
    color: "#fff",
  },
  dialogTitleDescarte: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2),
    backgroundColor: "#9E9E9E",
    color: "#fff",
  },
  dialogTitleDefault: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2),
    backgroundColor: "#FF9800",
    color: "#fff",
  },
  closeButton: {
    color: "#fff",
  },
  infoCard: {
    backgroundColor: "#FFF3E0",
    marginBottom: theme.spacing(2),
  },
  montoChip: {
    backgroundColor: "#FF9800",
    color: "#fff",
    fontWeight: 700,
    fontSize: 14,
  },
  btnDescarte: {
    backgroundColor: "#9E9E9E",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#757575",
    },
  },
}));

// ==================== CONSTANTES ====================

const TIPOS_CIERRE = [
  {
    value: "REEMBOLSADO",
    label: "Reembolsar al cliente",
    description: "El monto será devuelto al cliente",
    color: "#2196F3",
  },
  {
    value: "DESCARTADO",
    label: "Descartar (ajuste contable)",
    description: "El monto se registra como ajuste contable",
    color: "#9E9E9E",
  },
];

// ==================== COMPONENTE PRINCIPAL ====================

export default function ModalCerrarAbono({ open, onClose, abono }) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  // ==================== ESTADOS ====================

  const [tipoCierre, setTipoCierre] = useState("");
  const [categoriaId, setCategoriaId] = useState("");
  const [observaciones, setObservaciones] = useState("");

  // ==================== RTK QUERY ====================

  const [cerrarAbono, { isLoading: cerrando }] = useCerrarAbonoMutation();

  const { data: categoriasData } = useObtenerCategoriasContablesQuery({
    activo: true,
  });

  const categorias = categoriasData?.data || [];

  // ==================== EFECTOS ====================

  useEffect(() => {
    if (open) {
      setTipoCierre("");
      setCategoriaId("");
      setObservaciones("");
    }
  }, [open]);

  // ==================== HELPERS ====================

  const movimiento = abono?.movimiento || {};
  const bancoConfig = getBancoConfig(movimiento.banco);
  const montoAbono = parseFloat(abono?.monto_abono_pendiente || 0);
  const esMontoBajo = montoAbono < 1000;

  // Validaciones
  const esReembolso = tipoCierre === "REEMBOLSADO";
  const observacionesValidas = esReembolso
    ? observaciones.trim().length >= 10
    : true;
  const puedeConfirmar = tipoCierre && categoriaId && observacionesValidas;

  // Clase del título según tipo
  const getDialogTitleClass = () => {
    if (tipoCierre === "REEMBOLSADO") return classes.dialogTitleReembolso;
    if (tipoCierre === "DESCARTADO") return classes.dialogTitleDescarte;
    return classes.dialogTitleDefault;
  };

  // Título del modal
  const getTituloModal = () => {
    if (tipoCierre === "REEMBOLSADO") return "🔄 Reembolsar Abono";
    if (tipoCierre === "DESCARTADO") return "🗑️ Descartar Abono";
    return "📋 Cerrar Abono";
  };

  // ==================== HANDLERS ====================

  const handleConfirmar = async () => {
    if (!puedeConfirmar) return;

    try {
      const resultado = await cerrarAbono({
        conciliacion_id: abono.id,
        tipo_cierre: tipoCierre,
        categoria_id: parseInt(categoriaId),
        observaciones: observaciones.trim() || undefined,
      }).unwrap();

      enqueueSnackbar(resultado.mensaje || "Abono cerrado exitosamente", {
        variant: "success",
      });

      onClose();
    } catch (error) {
      enqueueSnackbar(error?.data?.message || "Error al cerrar abono", {
        variant: "error",
      });
    }
  };

  const handleClose = () => {
    if (!cerrando) {
      onClose();
    }
  };

  // ==================== RENDER ====================

  if (!abono) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableBackdropClick={cerrando}
      disableEscapeKeyDown={cerrando}
    >
      {/* ==================== HEADER ==================== */}
      <DialogTitle disableTypography className={getDialogTitleClass()}>
        <span style={{ fontSize: 18, fontWeight: 600 }}>
          {getTituloModal()}
        </span>
        <IconButton
          className={classes.closeButton}
          onClick={handleClose}
          disabled={cerrando}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* ==================== INFO DEL ABONO ==================== */}
        <Card className={classes.infoCard} variant="outlined">
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="subtitle2" color="textSecondary">
                    Abono a cerrar:
                  </Typography>
                  <Chip
                    label={formatearMonto(montoAbono)}
                    className={classes.montoChip}
                  />
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">
                  Banco
                </Typography>
                <Box>
                  <Chip
                    label={bancoConfig.label}
                    size="small"
                    style={{
                      backgroundColor: bancoConfig.bgColor,
                      color: bancoConfig.color || "#000",
                      fontWeight: 600,
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">
                  Fecha Conciliación
                </Typography>
                <Typography variant="body2">
                  {formatearFechaDisplay(abono.fecha_concilia)}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">
                  RUT Ordenante
                </Typography>
                <Typography variant="body2">
                  {formatearRutDisplay(movimiento.rut_ordenante)}
                  {movimiento.nombre_ordenante && (
                    <span style={{ color: "#666", marginLeft: 8 }}>
                      ({movimiento.nombre_ordenante})
                    </span>
                  )}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* ==================== ALERTA MONTO BAJO ==================== */}
        {esMontoBajo && (
          <Box mb={2}>
            <Alert severity="info">
              <strong>Monto bajo detectado:</strong> Este abono es menor a
              $1.000. Considera descartarlo como ajuste contable.
            </Alert>
          </Box>
        )}

        {/* ==================== SELECCIÓN TIPO CIERRE ==================== */}
        <Box mb={2}>
          <FormControl fullWidth variant="outlined">
            <InputLabel>Tipo de cierre *</InputLabel>
            <Select
              value={tipoCierre}
              onChange={(e) => setTipoCierre(e.target.value)}
              label="Tipo de cierre *"
            >
              {TIPOS_CIERRE.map((tipo) => (
                <MenuItem key={tipo.value} value={tipo.value}>
                  <Box>
                    <Typography variant="body2">{tipo.label}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {tipo.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* ==================== SELECCIÓN CATEGORÍA ==================== */}
        <Box mb={2}>
          <FormControl fullWidth variant="outlined" disabled={!tipoCierre}>
            <InputLabel>Categoría contable *</InputLabel>
            <Select
              value={categoriaId}
              onChange={(e) => setCategoriaId(e.target.value)}
              label="Categoría contable *"
            >
              {categorias.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  <Box>
                    <Typography variant="body2">{cat.nombre}</Typography>
                    {cat.descripcion && (
                      <Typography variant="caption" color="textSecondary">
                        {cat.descripcion}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* ==================== OBSERVACIONES ==================== */}
        <TextField
          fullWidth
          multiline
          rows={3}
          variant="outlined"
          label={
            esReembolso
              ? "Observaciones * (mín. 10 caracteres)"
              : "Observaciones (opcional)"
          }
          placeholder={
            esReembolso
              ? "Detalle el motivo del reembolso..."
              : "Observaciones adicionales..."
          }
          value={observaciones}
          onChange={(e) => setObservaciones(e.target.value)}
          error={
            esReembolso && observaciones.length > 0 && observaciones.length < 10
          }
          helperText={
            esReembolso && observaciones.length > 0 && observaciones.length < 10
              ? `Faltan ${10 - observaciones.length} caracteres`
              : ""
          }
        />

        {/* ==================== ALERTA REEMBOLSO ==================== */}
        {esReembolso && (
          <Box mt={2}>
            <Alert severity="warning">
              <strong>Importante:</strong> Al reembolsar, deberá realizar la
              transferencia al cliente de forma manual.
            </Alert>
          </Box>
        )}
      </DialogContent>

      {/* ==================== ACCIONES ==================== */}
      <DialogActions style={{ padding: 16, gap: 8 }}>
        <Button onClick={handleClose} disabled={cerrando} variant="outlined">
          Cancelar
        </Button>

        <LoadingButton
          variant="contained"
          color={tipoCierre === "REEMBOLSADO" ? "primary" : "inherit"}
          onClick={handleConfirmar}
          loading={cerrando}
          disabled={!puedeConfirmar}
          startIcon={
            tipoCierre === "REEMBOLSADO" ? <UndoIcon /> : <DeleteIcon />
          }
          className={tipoCierre === "DESCARTADO" ? classes.btnDescarte : ""}
        >
          {tipoCierre === "REEMBOLSADO"
            ? "Confirmar Reembolso"
            : tipoCierre === "DESCARTADO"
              ? "Confirmar Descarte"
              : "Confirmar"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
